import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { logger } from '@core/utils';
import { Toast } from '@core/components/ui';

import {
  FileUploadZone,
  FileUploadProgress,
  FileDeleteModal,
  FileTable,
} from '../components';
import { useFileUpload, useFileOperations } from '../hooks';

/**
 * FileManager Component (Backwards Compatibility Wrapper)
 *
 * This component provides backward compatibility for existing code
 * while using the new refactored components internally.
 *
 * @param {Object} props
 * @param {Array} props.files - Array of file objects
 * @param {Function} props.onFilesChange - Callback to refresh files list
 * @param {string|null} [props.userId] - User ID for file ownership
 * @param {string} props.accountId - Account ID for file ownership
 * @param {string|null} [props.segmentedKnowledgeId] - Segmented knowledge ID (optional)
 * @param {string|null} [props.folderId] - Folder ID to filter files (null for root, undefined for all)
 * @param {boolean} [props.showUploader=true] - Whether to show upload zone
 */
const FileManager = ({
  files: externalFiles,
  onFilesChange,
  userId = null,
  accountId,
  segmentedKnowledgeId = null,
  folderId = undefined,
  showUploader = true
}) => {
  const { t } = useTranslation();
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // File operations hook - using external files if provided
  const {
    files: internalFiles,
    isLoading,
    downloadFile,
    deleteFile,
    downloadingFile,
    error,
    successMessage,
    clearError,
    clearSuccess,
  } = useFileOperations({ t, userId, segmentedKnowledgeId, folderId });

  // Use external files if provided, otherwise use internal files from API
  const files = externalFiles || internalFiles;

  // File upload hook
  const {
    isDragging,
    uploadProgress,
    uploadingFiles,
    dragHandlers,
    handleFileInput,
  } = useFileUpload({
    userId,
    accountId,
    segmentedKnowledgeId,
    onSuccess: () => {
      // Call external callback if provided
      if (onFilesChange) {
        onFilesChange();
      }
    },
    onError: (err) => {
      logger.error('Upload error:', err);
    },
  });

  // Delete handlers - memoized to prevent unnecessary re-renders
  const handleDeleteClick = useCallback((file) => {
    setFileToDelete(file);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async (fileId) => {
    await deleteFile(fileId);
    setDeleteModalOpen(false);
    setFileToDelete(null);

    // Call external callback if provided
    if (onFilesChange) {
      onFilesChange();
    }
  }, [deleteFile, onFilesChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setFileToDelete(null);
  }, []);

  return (
    <>
      {/* Upload Zone */}
      {showUploader && (
        <div className="mb-6">
          <FileUploadZone
            onFileSelect={handleFileInput}
            disabled={uploadingFiles.size > 0}
            isDragging={isDragging}
            dragHandlers={dragHandlers}
          />
        </div>
      )}

      {/* Upload Progress */}
      <FileUploadProgress
        uploadingFiles={uploadingFiles}
        uploadProgress={uploadProgress}
      />

      {/* Files Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      ) : (
        <FileTable
          files={files}
          onDownload={downloadFile}
          onDelete={handleDeleteClick}
          downloadingFile={downloadingFile}
        />
      )}

      {/* Delete Confirmation Modal */}
      <FileDeleteModal
        isOpen={deleteModalOpen}
        file={fileToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Error Toast */}
      <Toast
        message={error || ''}
        type="error"
        isOpen={!!error}
        onClose={clearError}
      />

      {/* Success Toast */}
      <Toast
        message={successMessage || ''}
        type="success"
        isOpen={!!successMessage}
        onClose={clearSuccess}
      />
    </>
  );
};

FileManager.propTypes = {
  files: PropTypes.array,
  onFilesChange: PropTypes.func,
  userId: PropTypes.string,
  accountId: PropTypes.string,
  segmentedKnowledgeId: PropTypes.string,
  folderId: PropTypes.string,
  showUploader: PropTypes.bool,
};

export { FileManager };
